import React, { useEffect, useState, useMemo } from "react";
import { MapPin, ChevronDown, Search, Check } from "lucide-react";
import { api, citySlug } from "@/lib/api";
import { cn } from "@/lib/utils";
import { INDIAN_CITIES } from "@/data/indianCities";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";

export function CitySelector({ valueSlug, valueName, onSelect, align = "start", triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dbCities, setDbCities] = useState([]);

  useEffect(() => {
    if (open && dbCities.length === 0) api.get("/cities").then((r) => setDbCities(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line

  const q = query.trim().toLowerCase();
  const dbSlugs = useMemo(() => new Set(dbCities.map((c) => c.slug)), [dbCities]);
  const filteredDb = dbCities.filter((c) => !q || `${c.name} ${c.state}`.toLowerCase().includes(q));
  const filteredAll = useMemo(() => {
    const list = q ? INDIAN_CITIES.filter((c) => `${c.name} ${c.state}`.toLowerCase().includes(q)) : INDIAN_CITIES;
    return list.filter((c) => !dbSlugs.has(citySlug(c.name))).slice(0, 60);
  }, [q, dbSlugs]);
  const exactMatch = [...dbCities, ...INDIAN_CITIES].some((c) => c.name.toLowerCase() === q);

  const choose = (slug, name) => { onSelect(slug, name); setOpen(false); setQuery(""); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("rounded-full gap-1 h-10 px-3", triggerClassName)} data-testid="city-selector">
          <MapPin className="w-4 h-4 text-primary shrink-0" />
          <span className="text-sm font-semibold truncate max-w-[130px]">{valueName || "Select City"}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="p-0 w-72" data-testid="city-selector-popover">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search any Indian city…" data-testid="city-search-box" />
          <CommandList className="max-h-72">
            {filteredDb.length > 0 && (
              <CommandGroup heading="On OyeBest">
                {filteredDb.map((c) => (
                  <CommandItem key={`db-${c.slug}`} value={`db-${c.slug}`} onSelect={() => choose(c.slug, c.name)} data-testid={`city-option-${c.slug}`}>
                    <MapPin className="w-4 h-4 mr-2 text-primary" />
                    <span className="flex-1">{c.name}, {c.state}</span>
                    {typeof c.count === "number" && <span className="text-xs text-muted-foreground mr-1">{c.count}</span>}
                    {valueSlug === c.slug && <Check className="w-4 h-4 text-primary" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filteredAll.length > 0 && (
              <CommandGroup heading="All India">
                {filteredAll.map((c) => {
                  const s = citySlug(c.name);
                  return (
                    <CommandItem key={`all-${s}`} value={`all-${s}`} onSelect={() => choose(s, c.name)} data-testid={`city-option-${s}`}>
                      <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span className="flex-1">{c.name}, {c.state}</span>
                      {valueSlug === s && <Check className="w-4 h-4 text-primary" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {q && !exactMatch && (
              <CommandGroup heading="Explore">
                <CommandItem value="explore-custom" onSelect={() => choose(citySlug(query), query.trim())} data-testid="city-explore-custom">
                  <Search className="w-4 h-4 mr-2" /> Explore "{query.trim()}"
                </CommandItem>
              </CommandGroup>
            )}
            {!q && filteredDb.length === 0 && filteredAll.length === 0 && <CommandEmpty>Type a city name…</CommandEmpty>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
